/**
 * Makeup FLUX Prompt v3 — full-face presets built only from CUSTOM_CATEGORY_OPTIONS
 * (same colors / styles as the manual custom makeup panel).
 */

import { findCatalogOption } from '../../makeup-flux-lab/utils/catalogOptionMatch';
import {
  initialCustomLabForPreset,
  isLayerActive,
  noneLayer,
} from '../../makeup-flux-lab/utils/labMakeupSchema';
import { applyFinishToLayer } from '../../makeup-flux-lab/utils/makeupFluxLabFinishes';

/** When to wear the look — drives preset grouping in the UI. */
export const TIME_OF_USE_OPTIONS = [
  { id: 'morning', label: 'Morning', hint: 'Fresh, light, fast' },
  { id: 'day', label: 'Day / office', hint: 'Polished, camera-natural' },
  { id: 'evening', label: 'Evening', hint: 'Defined eyes & lip' },
  { id: 'night', label: 'Night / party', hint: 'Bold color & drama' },
];

function cloneLayer(layer) {
  if (!layer || typeof layer !== 'object') return noneLayer();
  return JSON.parse(JSON.stringify(layer));
}

/** Resolve a catalog option id to a lab layer (or None). */
export function layerFromCatalogOption(category, optionId) {
  if (!optionId || optionId === 'none') return noneLayer();
  const opt = findCatalogOption(category, optionId);
  return opt?.layer ? cloneLayer(opt.layer) : noneLayer();
}

/**
 * Build a full lab: every category set; slots are option ids or { optionId, intensity?, finishId? }.
 * @param {Record<string, string|object|null>} slots
 */
export function buildMakeupLabFromPresetSlots(slots) {
  const out = initialCustomLabForPreset({});
  for (const [category, spec] of Object.entries(slots)) {
    if (spec === null || spec === 'none') {
      out[category] = noneLayer();
      continue;
    }
    let optionId = spec;
    let intensity;
    let finishId;
    if (spec && typeof spec === 'object') {
      optionId = spec.optionId;
      intensity = spec.intensity;
      finishId = spec.finishId;
    }
    let layer = layerFromCatalogOption(category, optionId);
    if (typeof intensity === 'number' && layer && !layer.disabled) {
      const boostMul = category === 'eyeshadow' ? 1 : 1.2;
      const boosted = Math.min(95, Math.round(Math.round(intensity) * boostMul));
      layer = { ...layer, intensity: boosted };
    }
    if (finishId && layer && !layer.disabled) {
      layer = applyFinishToLayer(layer, finishId, category);
    }
    out[category] = layer;
  }
  return out;
}

/**
 * @typedef {object} MakeupV3TimePreset
 * @property {string} id
 * @property {string} timeOfUse — key from TIME_OF_USE_OPTIONS
 * @property {string} label
 * @property {string} description
 * @property {Record<string, string|object|null>} slots
 */

export const MAKEUP_V3_TIME_PRESETS = [
  {
    id: 'morning-sheer',
    timeOfUse: 'morning',
    label: 'Morning sheer',
    description: 'Sheer base, whisper blush, bare-minimum eyes, tinted lip.',
    slots: {
      foundation: { optionId: 'auto-skin', intensity: 14 },
      concealer: 'none',
      blush: { optionId: 'auto-blush', intensity: 16 },
      eyeshadow: 'none',
      eyeliner: 'minimal',
      eyelash: 'natural',
      lipstick: { optionId: 'auto-lip', intensity: 18 },
      highlighter: 'none',
      contour: 'none',
      eyebrows: 'none',
    },
  },
  {
    id: 'morning-peach',
    timeOfUse: 'morning',
    label: 'Peach wake-up',
    description: 'No base; peach cheeks, sand lids, nude lip.',
    slots: {
      foundation: 'none',
      concealer: 'none',
      blush: { optionId: '#F2B28F', intensity: 20, finishId: 'satin' },
      eyeshadow: { optionId: '#D5B190', intensity: 16, finishId: 'matte' },
      eyeliner: 'minimal',
      eyelash: 'natural',
      lipstick: { optionId: '#E8B89A', intensity: 22 },
      highlighter: 'none',
      contour: 'none',
      eyebrows: 'none',
    },
  },
  {
    id: 'day-office',
    timeOfUse: 'day',
    label: 'Office polish',
    description: 'Matched base, soft auto cheek/eye, tightline, harmonized lip.',
    slots: {
      foundation: { optionId: 'auto-skin', intensity: 20 },
      concealer: { optionId: 'auto-concealer', intensity: 14 },
      blush: { optionId: 'auto-blush', intensity: 18 },
      eyeshadow: { optionId: 'auto-eyeshadow', intensity: 16 },
      eyeliner: 'minimal',
      eyelash: 'natural',
      lipstick: { optionId: 'auto-lip', intensity: 26 },
      highlighter: 'none',
      contour: 'none',
      eyebrows: 'match',
    },
  },
  {
    id: 'day-soft-nude',
    timeOfUse: 'day',
    label: 'Soft nude day',
    description: 'Gemma-matched base, nude blush & gold dust lids, rose lip.',
    slots: {
      foundation: { optionId: 'auto-skin', intensity: 22, finishId: 'matte' },
      concealer: 'none',
      blush: { optionId: '#D5B190', intensity: 22, finishId: 'shimmer' },
      eyeshadow: { optionId: '#C9A050', intensity: 20, finishId: 'satin' },
      eyeliner: 'soft',
      eyelash: 'elegant',
      lipstick: { optionId: '#C78585', intensity: 24 },
      highlighter: 'none',
      contour: 'none',
      eyebrows: 'none',
    },
  },
  {
    id: 'evening-rose-gold',
    timeOfUse: 'evening',
    label: 'Rose gold evening',
    description: 'Gemma-matched base, terracotta blush, copper lids, smudge liner, berry lip.',
    slots: {
      foundation: { optionId: 'auto-skin', intensity: 26, finishId: 'satin' },
      concealer: { optionId: 'auto-concealer', intensity: 18 },
      blush: { optionId: '#C45C3A', intensity: 26, finishId: 'satin' },
      eyeshadow: { optionId: '#B8860B', intensity: 28, finishId: 'shimmer' },
      eyeliner: 'soft',
      eyelash: 'elegant',
      lipstick: { optionId: '#76153C', intensity: 30 },
      highlighter: { optionId: '#D4AF37', intensity: 22, finishId: 'shimmer' },
      contour: 'none',
      eyebrows: 'match',
    },
  },
  {
    id: 'evening-smoky',
    timeOfUse: 'evening',
    label: 'Smoky taupe',
    description: 'Gemma-matched base, cocoa rose cheeks, smoky taupe eyes, wine lip.',
    slots: {
      foundation: { optionId: 'auto-skin', intensity: 28, finishId: 'satin' },
      concealer: { optionId: 'auto-concealer', intensity: 20 },
      blush: { optionId: '#5A2A30', intensity: 24, finishId: 'satin' },
      eyeshadow: { optionId: '#6B5A4E', intensity: 30, finishId: 'matte' },
      eyeliner: 'soft',
      eyelash: 'elegant',
      lipstick: { optionId: '#722F37', intensity: 32 },
      highlighter: 'none',
      contour: 'none',
      eyebrows: 'match',
    },
  },
  {
    id: 'night-party',
    timeOfUse: 'night',
    label: 'Party glam',
    description: 'Gemma-matched base, bold blush, berry smoke eyes, cat eye & lashes, fuchsia lip.',
    slots: {
      foundation: { optionId: 'auto-skin', intensity: 32, finishId: 'satin' },
      concealer: { optionId: 'auto-concealer', intensity: 24 },
      blush: { optionId: '#F0648A', intensity: 32, finishId: 'shimmer' },
      eyeshadow: { optionId: '#A6525B', intensity: 34, finishId: 'shimmer' },
      eyeliner: 'cat_eye',
      eyelash: 'cat_eye_lash',
      lipstick: { optionId: '#BF124D', intensity: 36 },
      highlighter: { optionId: '#F5D0D8', intensity: 28, finishId: 'dewy' },
      contour: 'none',
      eyebrows: 'match',
    },
  },
  {
    id: 'night-red-carpet',
    timeOfUse: 'night',
    label: 'Red carpet',
    description: 'Gemma-matched base, plum cheek, gold copper lids, classic red lip.',
    slots: {
      foundation: { optionId: 'auto-skin', intensity: 34, finishId: 'satin' },
      concealer: { optionId: 'auto-concealer', intensity: 26 },
      blush: { optionId: '#B14D7A', intensity: 28, finishId: 'satin' },
      eyeshadow: { optionId: '#B8860B', intensity: 36, finishId: 'shimmer' },
      eyeliner: 'cat_eye',
      eyelash: 'wispy',
      lipstick: { optionId: '#FF0000', intensity: 38 },
      highlighter: { optionId: '#D4AF37', intensity: 30, finishId: 'shimmer' },
      contour: 'none',
      eyebrows: 'match',
    },
  },
  {
    id: 'ceremony-indian',
    timeOfUse: 'ceremony',
    label: 'Indian bridal',
    description: 'Satin base, warm rose blush, gold copper eyes, burgundy lip. Pick a regional jewellery style below.',
    bridalOrnaments: true,
    slots: {
      foundation: { optionId: 'auto-skin', intensity: 38, finishId: 'satin' },
      concealer: { optionId: 'auto-concealer', intensity: 32 },
      blush: { optionId: '#C97B6A', intensity: 30, finishId: 'satin' },
      eyeshadow: { optionId: '#B8860B', intensity: 26, finishId: 'satin' },
      eyeliner: 'soft',
      eyelash: 'elegant',
      lipstick: { optionId: '#5A0E24', intensity: 42, finishId: 'matte' },
      highlighter: { optionId: '#D4AF37', intensity: 16, finishId: 'satin' },
      contour: 'none',
      eyebrows: 'match',
    },
  },
  {
    id: 'ceremony-bridal-soft',
    timeOfUse: 'ceremony',
    label: 'Bridal soft',
    description: 'Glow base, soft pink blush, champagne lids, baby rose lip. Optional regional bridal jewellery.',
    bridalOrnaments: true,
    slots: {
      foundation: { optionId: 'auto-skin', intensity: 30, finishId: 'dewy' },
      concealer: { optionId: 'auto-concealer', intensity: 24 },
      blush: { optionId: '#F7AFC1', intensity: 28, finishId: 'satin' },
      eyeshadow: { optionId: '#C9A050', intensity: 24, finishId: 'satin' },
      eyeliner: 'minimal',
      eyelash: 'soft',
      lipstick: { optionId: '#E8A8B0', intensity: 34 },
      highlighter: { optionId: '#F5D0D8', intensity: 28, finishId: 'dewy' },
      contour: 'none',
      eyebrows: 'match',
    },
  },
];

export function getMakeupV3PresetById(presetId) {
  return MAKEUP_V3_TIME_PRESETS.find((p) => p.id === presetId) || null;
}

export function makeupLabForV3Preset(preset) {
  if (!preset?.slots) return initialCustomLabForPreset({});
  return buildMakeupLabFromPresetSlots(preset.slots);
}

/** Everyday presets only — bridal looks use MakeupV3BridalLooksGrid. */
export const MAKEUP_V3_EVERYDAY_PRESETS = MAKEUP_V3_TIME_PRESETS.filter(
  (p) => p.timeOfUse !== 'ceremony' && !p.bridalOrnaments,
);

export function presetsForTimeOfUse(timeOfUseId) {
  if (!timeOfUseId) return MAKEUP_V3_EVERYDAY_PRESETS;
  return MAKEUP_V3_EVERYDAY_PRESETS.filter((p) => p.timeOfUse === timeOfUseId);
}

export function timeOfUseLabel(timeOfUseId) {
  return TIME_OF_USE_OPTIONS.find((t) => t.id === timeOfUseId)?.label || timeOfUseId;
}

const AUTO_SWATCH_FALLBACK = {
  foundation: '#d4b896',
  concealer: '#e8dcc8',
  blush: '#e8a0a0',
  eyeshadow: '#b8956c',
  lipstick: '#c78585',
  highlighter: '#f5d0d8',
};

/** Hex colors for preset card swatches (fixed slots + auto-category hints). */
export function presetV3SwatchColors(preset) {
  const lab = makeupLabForV3Preset(preset);
  const order = ['lipstick', 'blush', 'eyeshadow', 'highlighter', 'foundation', 'concealer'];
  const colors = [];
  for (const cat of order) {
    const layer = lab[cat];
    if (!isLayerActive(layer)) continue;
    const hex = layer?.color;
    if (hex && /^#[0-9A-Fa-f]{3,8}$/.test(hex)) {
      colors.push(hex);
      continue;
    }
    if (AUTO_SWATCH_FALLBACK[cat]) colors.push(AUTO_SWATCH_FALLBACK[cat]);
  }
  if (!colors.length && preset?.slots) {
    for (const spec of Object.values(preset.slots)) {
      const id = spec && typeof spec === 'object' ? spec.optionId : spec;
      if (typeof id === 'string' && /^#[0-9A-Fa-f]{6}$/.test(id)) colors.push(id);
    }
  }
  return colors.slice(0, 5);
}

export function presetV3SwatchBackground(preset) {
  const colors = presetV3SwatchColors(preset);
  if (!colors.length) return 'linear-gradient(135deg, #c78585, #e8a0a0, #b8956c)';
  if (colors.length === 1) return colors[0];
  const stops = colors.map((c, i) => `${c} ${Math.round((i / (colors.length - 1)) * 100)}%`);
  return `linear-gradient(90deg, ${stops.join(', ')})`;
}
